import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  BookOpen, ChartLineUp, ShieldCheck, ArrowRight,
  Lightning, ClockCountdown, CurrencyCircleDollar, ChartBar
} from '@phosphor-icons/react';
import axios from 'axios';
import SEO from '../components/SEO';
import { OrganizationSchema, FAQSchema } from '../components/StructuredData';
import ShareButtons from '../components/ShareButtons';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', monthly_revenue: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/leads`, { ...formData, source: 'homepage' });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Daily AI-Bookkeeping + Fractional CFO"
        description="Accurate books daily. CFO insights monthly. Bundle bookkeeping, compliance, and FP&A for your VC-backed SaaS, D2C, or cross-border business. India, USA, UAE."
        path="/"
      />
      <OrganizationSchema />
      <FAQSchema faqs={[
        { question: 'What is Ventriqo?', answer: 'Ventriqo is a Finance-as-a-Service company offering daily AI-bookkeeping, multi-jurisdiction compliance, and fractional CFO services for growing businesses in India, USA, and UAE.' },
        { question: 'What is T+1 posting?', answer: 'T+1 means every cleared bank transaction is categorized and reconciled within 24 hours, giving you daily financial visibility instead of monthly surprises.' },
        { question: 'Which jurisdictions do you cover?', answer: 'We handle compliance across India (GST/TDS), USA (Sales Tax/1099), and UAE (VAT/Corporate Tax).' },
        { question: 'Is there a lock-in period?', answer: 'No. Keep what we set up even if you leave. No lock-in contracts.' },
      ]} />

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6">
                Finance-as-a-Service
              </p>
              <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none text-black mb-6">
                Accurate Books.<br />Daily.<br />
                <span className="text-[#002FA7]">CFO Insights.</span><br />Monthly.
              </h1>
              <p className="text-base leading-relaxed text-gray-600 max-w-lg mb-8">
                Daily-updated books + proactive insights, not month-end chaos.
                Bundle bookkeeping, compliance, and light FP&A for your business.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#lead-form">
                  <Button data-testid="hero-cta-primary" className="bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] px-8 py-3 text-sm font-semibold">
                    Get Free Finance Health Check
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </a>
                <Link to="/services">
                  <Button data-testid="hero-cta-secondary" variant="outline" className="border-black text-black hover:bg-black hover:text-white rounded-[4px] px-8 py-3 text-sm font-semibold">
                    View Services
                  </Button>
                </Link>
              </div>
              <div className="mt-6">
                <ShareButtons url="https://www.ventriqo.com" title="Ventriqo - Daily AI-Bookkeeping + Fractional CFO" />
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxmaW5hbmNlJTIwZGFzaGJvYXJkJTIwY2hhcnRzfGVufDB8fHx8MTc3NTgwMzYwOXww&ixlib=rb-4.1.0&q=85"
                alt="Finance Dashboard"
                className="w-full border border-black/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Strip */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: 'Posting SLA', value: 'T+1', icon: ClockCountdown },
            { label: 'Classification Accuracy', value: '>98%', icon: Lightning },
            { label: 'Monthly Close', value: 'T+3-5', icon: ChartBar },
            { label: 'Support Hours', value: '10-7 IST', icon: CurrencyCircleDollar },
          ].map((m, i) => (
            <div key={i} className="text-center">
              <m.icon size={24} className="mx-auto mb-2 text-[#002FA7]" weight="duotone" />
              <p className="text-2xl font-black tracking-tight">{m.value}</p>
              <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">{m.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">What We Do</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-12">
            End-to-end finance operations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10">
            {[
              {
                icon: BookOpen,
                title: 'Daily Bookkeeping',
                desc: 'Bank-reconciled books at T+1. Cash/burn tracking, AR/AP aging, and exception queue management with automated data ingestion.',
              },
              {
                icon: ShieldCheck,
                title: 'Multi-Jurisdiction Compliance',
                desc: 'GST, TDS/Withholding Tax, VAT, Corp Tax across India, US and UAE. We handle the filings so you stay compliant everywhere.',
              },
              {
                icon: ChartLineUp,
                title: 'FP&A / Fractional CFO',
                desc: 'Budgeting, forecasting, unit economics (LTV/CAC), board packs, cash runway, fundraising support, and cross-border structuring.',
              },
            ].map((s, i) => (
              <div key={i} className={`p-8 md:p-12 ${i < 2 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                <s.icon size={32} weight="duotone" className="text-[#002FA7] mb-4" />
                <h3 className="text-xl font-bold tracking-tight text-black mb-3">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                <Link to="/services" className="text-sm font-semibold text-[#002FA7] mt-4 inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Learn more <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">How It Works</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-12">
            From raw data to boardroom insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Data Ingestion', desc: 'We connect to your banks, gateways, and accounting tools automatically.' },
              { step: '02', title: 'AI Categorization', desc: 'ML-powered transaction classification with 98%+ accuracy.' },
              { step: '03', title: 'Human Review', desc: 'Expert reviewers check exceptions and approve postings daily.' },
              { step: '04', title: 'Daily Digest', desc: 'Cash, receipts, AR, bills due, and anomalies delivered to you.' },
            ].map((s, i) => (
              <div key={i} className="relative">
                <span className="text-6xl font-black text-black/5 absolute -top-2 -left-2">{s.step}</span>
                <div className="pt-10">
                  <h3 className="text-lg font-bold text-black mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials / Social Proof */}
      <section data-testid="social-proof" className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Trusted By</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-12">
            Founders who chose clarity over chaos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10">
            {[
              {
                quote: "Ventriqo gave us real-time visibility into our burn rate. We went from monthly surprises to daily confidence.",
                name: "Rahul M.",
                role: "CEO, Series A SaaS",
              },
              {
                quote: "Their fractional CFO helped us prepare our data room in 3 weeks. We closed our round faster than expected.",
                name: "Sarah K.",
                role: "Founder, D2C Brand",
              },
              {
                quote: "Cross-border compliance was a nightmare. Ventriqo handles India GST and US sales tax seamlessly.",
                name: "James L.",
                role: "CTO, US-India Entity",
              },
            ].map((t, i) => (
              <div key={i} className={`p-8 md:p-10 ${i < 2 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                <p className="text-sm text-gray-700 leading-relaxed mb-6 italic">"{t.quote}"</p>
                <p className="text-sm font-bold text-black">{t.name}</p>
                <p className="text-xs text-gray-500">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section id="lead-form" data-testid="lead-form-section" className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4 text-center">Free Assessment</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-3 text-center">
              Get Your Free Finance Health Check
            </h2>
            <p className="text-sm text-gray-600 text-center mb-8">
              Keep what we set up even if you leave. No lock-in.
            </p>
            {submitted ? (
              <div data-testid="lead-form-success" className="text-center py-12 border border-[#002FA7]/20 bg-white">
                <p className="text-xl font-bold text-black mb-2">Thank you!</p>
                <p className="text-sm text-gray-600">We'll reach out within 24 hours with your finance health check.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-black/10 p-8">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-black">Full Name</Label>
                  <Input data-testid="lead-form-name" id="name" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7] focus:border-transparent" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-black">Work Email</Label>
                  <Input data-testid="lead-form-email" id="email" type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7] focus:border-transparent" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-black">Phone</Label>
                  <Input data-testid="lead-form-phone" id="phone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7] focus:border-transparent" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-sm font-medium text-black">Company Name</Label>
                  <Input data-testid="lead-form-company" id="company" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7] focus:border-transparent" />
                </div>
                <div>
                  <Label htmlFor="revenue" className="text-sm font-medium text-black">Monthly Revenue (approx)</Label>
                  <Input data-testid="lead-form-revenue" id="revenue" value={formData.monthly_revenue} onChange={e => setFormData({...formData, monthly_revenue: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7] focus:border-transparent" />
                </div>
                <Button data-testid="lead-form-submit" type="submit" disabled={submitting} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] py-3 text-sm font-semibold">
                  {submitting ? 'Submitting...' : 'Get My Free Finance Health Check'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
